"use client"

import { useEffect, useState, useCallback } from "react"
import { motion, AnimatePresence } from "framer-motion"

// Konami Code: ↑ ↑ ↓ ↓ ← → ← → B A
const KONAMI_CODE = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "KeyB",
  "KeyA",
]

export function EasterEgg() {
  const [isActive, setIsActive] = useState(false)
  const [inputSequence, setInputSequence] = useState<string[]>([])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const newSequence = [...inputSequence, e.code].slice(-KONAMI_CODE.length)
      setInputSequence(newSequence)

      // Check if the sequence matches Konami code
      if (newSequence.length === KONAMI_CODE.length) {
        const isMatch = newSequence.every((key, i) => key === KONAMI_CODE[i])
        if (isMatch) {
          setIsActive(true)
          setInputSequence([])
          // Auto-hide after 5 seconds
          setTimeout(() => setIsActive(false), 5000)
        }
      }
    },
    [inputSequence]
  )

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          className="fixed inset-0 z-[300] flex items-center justify-center pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Confetti-like particles */}
          {Array.from({ length: 50 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-3 h-3 rounded-full"
              style={{
                backgroundColor: [
                  "var(--color-accent-gold)",
                  "var(--color-accent-teal)",
                  "var(--color-accent-copper)",
                ][i % 3],
              }}
              initial={{
                x: 0,
                y: 0,
                scale: 0,
                opacity: 1,
              }}
              animate={{
                x: (Math.random() - 0.5) * window.innerWidth,
                y: (Math.random() - 0.5) * window.innerHeight,
                scale: [0, 1, 0],
                opacity: [1, 1, 0],
                rotate: Math.random() * 360,
              }}
              transition={{
                duration: 2 + Math.random(),
                ease: "easeOut",
                delay: Math.random() * 0.3,
              }}
            />
          ))}

          {/* Secret message */}
          <motion.div
            className="relative z-10 text-center"
            initial={{ scale: 0, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 10 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <motion.p
              className="text-6xl md:text-8xl font-bold bg-gradient-to-r from-[var(--color-accent-gold)] via-[var(--color-accent-teal)] to-[var(--color-accent-copper)] bg-clip-text text-transparent"
              animate={{
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
              }}
              transition={{ duration: 3, repeat: Infinity }}
              style={{
                backgroundSize: "200% 200%",
                fontFamily: "var(--font-display)",
              }}
            >
              🎮 You found me!
            </motion.p>
            <motion.p
              className="mt-4 text-lg text-[var(--color-text-secondary)]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Now go build something extraordinary.
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Simple click counter Easter egg - reveals message after specific number of clicks
interface ClickCounterEggProps {
  children: React.ReactNode
  targetClicks?: number
  message?: string
}

export function ClickCounterEgg({
  children,
  targetClicks = 7,
  message = "You're persistent! I like that.",
}: ClickCounterEggProps) {
  const [clicks, setClicks] = useState(0)
  const [showMessage, setShowMessage] = useState(false)

  const handleClick = () => {
    const newClicks = clicks + 1
    setClicks(newClicks)

    if (newClicks >= targetClicks) {
      setShowMessage(true)
      setTimeout(() => {
        setShowMessage(false)
        setClicks(0)
      }, 3000)
    }
  }

  return (
    <div className="relative" onClick={handleClick}>
      {children}
      <AnimatePresence>
        {showMessage && (
          <motion.div
            className="absolute -top-12 left-1/2 -translate-x-1/2 whitespace-nowrap px-4 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-accent-gold)] rounded-lg text-sm text-[var(--color-text-primary)] z-50"
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.9 }}
          >
            {message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
